// Shape of src/data/price-reference-teaser.json — the build-time snapshot of
// the monthly price-reference edition that feeds /tabela.
//
// Why a committed JSON and not a runtime fetch: the full table is the gated
// lead magnet, so there is no public API that serves it, and adding one would
// hand scrapers the thing we're asking for an email in exchange for. The
// teaser is a fixed 15 rows that only change once a month, so a build
// artifact is the cheaper and safer answer.
//
// Refreshed by scripts/fetch-price-reference.ts, which deploy.yml runs before
// `vite build` — twice a day, after each scraper pass. Both render paths read
// this same file (the SPA route imports it, the prerender reads it off disk),
// so the static HTML and the hydrated page can never disagree.

import raw from "@/data/price-reference-teaser.json";

export type PriceReferenceRow = {
  label: string;
  /** Exact model_slug of the cohort (the grain the table is computed on). */
  modelSlug: string;
  year: number;
  n: number;
  pricep10: number;
  pricep50: number;
  pricep90: number;
  kmp50: number | null;
  cutSharePct: number | null;
};

export type PriceReferenceTeaser = {
  /** First day of the edition month, YYYY-MM-DD. */
  editionMonth: string;
  /** When these numbers were measured, YYYY-MM-DD. */
  generatedAt: string;
  /** The `minN` threshold the rows were filtered to. */
  minN: number;
  /** Edition-level scalars. Absent in the seed file — the published post's
   *  counts came from a slightly different query day and inventing matching
   *  numbers here would put two different totals in front of the reader. */
  meta?: {
    carsTotal: number;
    cohortsGe10: number;
    cohortsGe20: number;
  };
  rows: PriceReferenceRow[];
};

// Single typed handle on the data artifact. The route needs the edition dates
// for its Dataset JSON-LD and the body needs the rows; importing the raw JSON
// in two places invites the two from drifting on the cast.
export const teaser: PriceReferenceTeaser = raw;

/** Public name of the series. Appears as the page masthead, in the title tag
 *  and as the Dataset `name` — keep the three in sync. */
export const SERIES_NAME = "Tabela Busca Blindados";

const MONTHS_PT = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

/** "2026-10-01" → "outubro de 2026" */
export function editionMonthPt(iso: string): string {
  const [y, m] = iso.split("-").map(Number);
  return `${MONTHS_PT[m - 1]} de ${y}`;
}

/** /buscar link for one cohort: exact version (model_slug) + exact model year. */
export function cohortSearchHref(r: Pick<PriceReferenceRow, "label" | "modelSlug" | "year">): string {
  const qs = new URLSearchParams({
    versao: r.modelSlug,
    versao_label: r.label,
    min_year: String(r.year),
    max_year: String(r.year),
  });
  return `/buscar/?${qs}`;
}

export function brl(v: number | null): string {
  if (v == null) return "—";
  return `R$ ${v.toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`;
}

export function num(v: number | null): string {
  if (v == null) return "—";
  return v.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
}

export function pct(v: number | null): string {
  if (v == null) return "—";
  return `${Math.round(v)}%`;
}
